import React, { useEffect } from 'react'
import { X, Maximize2 } from 'lucide-react'
import { cn } from '../../lib/utils'
import { Button } from './button'

interface ModalProps {
  isOpen: boolean
  onClose: () => void
  title?: string
  children: React.ReactNode
  className?: string
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full'
  showHeader?: boolean
}

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  className,
  size = 'lg',
  showHeader = true
}) => {
  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      document.body.style.overflow = 'hidden'
    }

    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = 'unset'
    }
  }, [isOpen, onClose])

  if (!isOpen) return null

  const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-4xl',
    xl: 'max-w-6xl',
    full: 'max-w-[95vw] max-h-[95vh]'
  }

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center'>
      {/* Backdrop */}
      <div
        className='fixed inset-0 bg-black/50 backdrop-blur-sm'
        onClick={onClose}
      />

      {/* Modal */}
      <div
        className={cn(
          'relative mx-4 w-full rounded-xl border border-gray-200 bg-white shadow-2xl',
          'flex max-h-[95vh] flex-col',
          sizeClasses[size],
          className
        )}
      >
        {/* Header */}
        {showHeader && (
          <div className='flex items-center justify-between border-b border-gray-200 p-6'>
            <h2 className='text-xl font-semibold text-gray-900'>
              {title || 'Expanded View'}
            </h2>
            <Button
              variant='ghost'
              size='icon'
              onClick={onClose}
              className='h-8 w-8 rounded-full hover:bg-gray-100'
            >
              <X className='h-4 w-4' />
            </Button>
          </div>
        )}

        {/* Content - Always scrollable */}
        <div
          className={cn(
            'flex-1 overflow-y-auto p-6',
            size === 'full' && 'h-full',
            !showHeader && 'pt-6'
          )}
        >
          {children}
        </div>
      </div>
    </div>
  )
}

interface ExpandableWrapperProps {
  children: React.ReactNode
  title: string
  expandedContent?: React.ReactNode
  className?: string
  expandButtonClassName?: string
  showExpandButton?: boolean
}

export const ExpandableWrapper: React.FC<ExpandableWrapperProps> = ({
  children,
  title,
  expandedContent,
  className,
  expandButtonClassName,
  showExpandButton = true
}) => {
  const [isExpanded, setIsExpanded] = React.useState(false)

  return (
    <div className={cn('group relative', className)}>
      {/* Expand Button - Only show if enabled */}
      {showExpandButton && (
        <Button
          variant='ghost'
          size='icon'
          onClick={() => setIsExpanded(true)}
          className={cn(
            'absolute right-2 top-2 z-10 h-8 w-8 bg-white/80 opacity-0 shadow-sm transition-opacity duration-200 hover:bg-white group-hover:opacity-100',
            expandButtonClassName
          )}
          title='Expand to fullscreen'
        >
          <Maximize2 className='h-4 w-4' />
        </Button>
      )}

      {/* Original Content */}
      {children}

      {/* Fullscreen Modal */}
      <Modal
        isOpen={isExpanded}
        onClose={() => setIsExpanded(false)}
        title={title}
        size='full'
      >
        {expandedContent || children}
      </Modal>
    </div>
  )
}

export default Modal
